import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { getCustomer } from '../api/authApi';
import { colorTokens } from '../app/designTokens';
import { CustomerBillingTab } from '../components/CustomerBillingTab';
import { CustomerCompanyDataPanel, CustomerTestCleanupPanel } from '../components/CustomerCleanupAndCompanyPanels';
import { CustomerEntitlementsTab } from '../components/CustomerEntitlementsTab';
import { CustomerUsersTab } from '../components/CustomerUsersTab';
import { DataTable } from '../components/DataTable';
import { InfoCard } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { PlaceholderPanel } from '../components/PlaceholderPanel';
import { PaymentsTable } from '../components/PaymentsTable';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { SubscriptionsTable } from '../components/SubscriptionsTable';
import { SupportTicketsTable } from '../components/SupportTicketsTable';
import { Tab, Tabs } from '../components/ui/Tabs';

const { semantic } = colorTokens;

export function CustomerDetailPage() {
  const { t, i18n } = useTranslation(['customers', 'common', 'statuses']);
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  const tabs = useMemo(() => [
    t('customers:tabs.overview'),
    t('customers:tabs.company_data'),
    t('customers:tabs.tenants'),
    t('customers:tabs.subscription'),
    t('customers:tabs.billing'),
    t('customers:tabs.payments'),
    t('customers:tabs.support'),
    t('customers:tabs.users'),
    t('customers:tabs.access'),
    t('customers:tabs.audit'),
  ], [t]);

  const formatDate = (value) => {
    if (!value) {
      return t('common:empty_value');
    }

    return new Date(value).toLocaleString(i18n.language);
  };

  const translateStatus = (group, value) => {
    if (!value) {
      return t('common:empty_value');
    }

    const key = `statuses:${group}.${value}`;
    return i18n.exists(key) ? t(key) : value;
  };

  const translateAccess = (allowed) => (
    allowed ? t('statuses:access.allowed') : t('statuses:access.blocked')
  );

  useEffect(() => {
    setLoading(true);
    setError(null);

    getCustomer(id)
      .then(setCustomer)
      .catch(() => setError(t('customers:load_detail_failed')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const emptyValue = t('common:empty_value');
  const tenants = customer?.tenants || [];

  const tenantColumns = useMemo(
    () => [
      {
        id: 'name',
        label: t('common:name'),
        render: (row) => row.name || emptyValue,
      },
      {
        id: 'slug',
        label: t('common:slug'),
        render: (row) => row.slug || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="tenant" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'access',
        label: t('common:access'),
        render: (row) => translateAccess(row.is_access_allowed),
      },
    ],
    // translateAccess depends on t; emptyValue/t cover that
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emptyValue, t],
  );

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (error || !customer) {
    return (
      <SectionCard variant="Danger">
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {error || t('customers:not_found')}
        </Alert>
      </SectionCard>
    );
  }

  return (
    <Box>
      <PageHeader
        type="Detail"
        title={customer.name}
        subtitle={customer.slug}
        backTo="/customers"
        badge={(
          <StatusBadge
            status={customer.status}
            category="customer"
            size="Md"
          />
        )}
      />

      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        {tabs.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard label={t('common:status')}>
              <StatusBadge status={customer.status} category="customer" size="Sm" />
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              label={t('common:tenants')}
              value={customer.tenants_count ?? tenants.length}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              label={t('customers:fields.billing_email')}
              value={customer.billing_email || emptyValue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              label={t('customers:fields.stripe_customer')}
              value={customer.stripe_customer_id || emptyValue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              label={t('customers:fields.stripe_sync')}
              value={customer.stripe_sync_status || emptyValue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              label={t('common:created')}
              value={formatDate(customer.created_at)}
            />
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <CustomerCompanyDataPanel customer={customer} translateStatus={translateStatus} />
      )}

      {tab === 2 && (
        <SectionCard variant="Default" noPadding>
          <DataTable
            type="Default"
            embedded
            columns={tenantColumns}
            rows={tenants}
            emptyMessage={t('customers:no_tenants')}
            onRowClick={(row) => navigate(`/tenants/${row.id}`)}
          />
        </SectionCard>
      )}

      {tab === 3 && (
        <SectionCard variant="Default">
          <Typography
            sx={{ mb: 2, fontSize: 13, color: semantic.text.secondary }}
          >
            {t('customers:subscriptions_hint')}
          </Typography>
          <SubscriptionsTable customerId={customer.id} emptyMessage={t('customers:no_subscriptions')} />
        </SectionCard>
      )}

      {tab === 4 && (
        <CustomerBillingTab customerId={customer.id} />
      )}

      {tab === 5 && (
        <SectionCard variant="Default">
          <Typography sx={{ mb: 2, fontSize: 13, color: semantic.text.secondary }}>
            {t('customers:payments_hint')}
          </Typography>
          <PaymentsTable customerId={customer.id} emptyMessage={t('customers:no_payments')} />
        </SectionCard>
      )}

      {tab === 6 && (
        <SectionCard variant="Default">
          <Typography sx={{ mb: 2, fontSize: 13, color: semantic.text.secondary }}>
            {t('customers:support_hint')}
          </Typography>
          <SupportTicketsTable
            customerId={customer.id}
            emptyMessage={t('customers:no_support_tickets')}
          />
        </SectionCard>
      )}

      {tab === 7 && (
        <CustomerUsersTab customerId={customer.id} />
      )}

      {tab === 8 && (
        <CustomerEntitlementsTab customerId={customer.id} customer={customer} />
      )}

      {tab === 9 && (
        <>
          <SectionCard variant="Default" title={t('customers:audit_title')}>
            <PlaceholderPanel message={t('customers:audit_message')} />
          </SectionCard>
          <Box sx={{ mt: 3 }}>
            <CustomerTestCleanupPanel customer={customer} />
          </Box>
        </>
      )}
    </Box>
  );
}
